import { createBrowserRouter, Navigate } from 'react-router-dom';
import { MainLayout } from '@shared/components/Layout';
import { ProtectedRoute } from '@shared/components/ProtectedRoute';
import { LoginPage } from '@features/auth/LoginPage';
import { TokenLoginPage } from '@features/auth/TokenLoginPage';
import { DashboardPage } from '@features/dashboard/DashboardPage';
import { PlayersPage } from '@features/players/PlayersPage';
import { SponsorsPage } from '@features/sponsors/SponsorsPage';
import { PostsPage } from '@features/posts/PostsPage';
import { MatchesPage } from '@features/matches/MatchesPage';
import { MatchLivePage } from '@features/matches/MatchLivePage';
import { StandingsPage } from '@features/standings/StandingsPage';
import { TicketStatsPage } from '@features/ticket-stats/TicketStatsPage';
import { ProductsPage } from '@features/products/ProductsPage';
import { ShopSettingsPage } from '@features/shop-settings/ShopSettingsPage';
import { CouponsPage } from '@features/coupons/CouponsPage';
import { DiscountRulesPage } from '@features/discount-rules/DiscountRulesPage';
import { OrdersPage } from '@features/orders/OrdersPage';
import { InventoryPage } from '@features/inventory/InventoryPage';
import { PartnersPage } from '@features/partners/PartnersPage';
import { PollsPage } from '@features/polls/PollsPage';
import { PushPage } from '@features/push/PushPage';
import { AuditPage } from '@features/audit/AuditPage';
import { SettingsPage } from '@features/settings/SettingsPage';
import { EmailTemplatesPage } from '@features/settings/email-templates/EmailTemplatesPage';
import { LegalPagesPage } from '@features/settings/legal-pages/LegalPagesPage';
import { AiSettingsPage } from '@features/settings/AiSettingsPage';
import { EmailTemplateEditor } from '@features/settings/email-templates/EmailTemplateEditor';
import { PaymentSettingsPage } from '@features/payment-settings/PaymentSettingsPage';
import { ReceiptsPage } from '@features/receipts/ReceiptsPage';
import { ReceiptInspectorPage } from '@features/receipts/ReceiptInspectorPage';
import { WatchlistPage } from '@features/receipts/WatchlistPage';
import { FansPage } from '@features/fans/FansPage';
import { SeasonTicketsPage } from '@features/season-tickets/SeasonTicketsPage';
import { RewardsPage } from '@features/rewards/RewardsPage';
import { LeaderboardPage } from '@features/leaderboard/LeaderboardPage';
import { LoyaltyPeriodsPage } from '@features/loyalty-periods/LoyaltyPeriodsPage';
import { TenantsPage } from '@features/admin-tenants/TenantsPage';
import { AudiencePage } from '@features/analytics/pages/AudiencePage';
import { StorePage } from '@features/analytics/pages/StorePage';
import { LoyaltyPage } from '@features/analytics/pages/LoyaltyPage';
import { SponsorsPage as AnalyticsSponsorsPage } from '@features/analytics/pages/SponsorsPage';
import { SetupPage } from '@features/setup/SetupPage';
import { StaffPage } from '@features/staff/StaffPage';
import { BadgesPage } from '@features/badges/BadgesPage';
import { CheckinPage } from '@features/checkin/CheckinPage';
import { HeroBannersPage } from '@features/hero-banners/HeroBannersPage';
import { BannedWordsPage } from '@features/moderation/BannedWordsPage';
import { MediaLibraryPage } from '@features/media-library/MediaLibraryPage';
import { RssImportPage } from '@features/rss/RssImportPage';

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  { path: '/auth/token', element: <TokenLoginPage /> },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <ProtectedRoute roles={['CLUB_ADMIN']}><DashboardPage /></ProtectedRoute> },
      { path: 'matches', element: <MatchesPage /> },
      { path: 'matches/:id/live', element: <MatchLivePage /> },
      { path: 'standings', element: <StandingsPage /> },
      { path: 'ticket-stats', element: <ProtectedRoute roles={['CLUB_ADMIN']}><TicketStatsPage /></ProtectedRoute> },
      { path: 'roster', element: <PlayersPage /> },
      { path: 'orders', element: <OrdersPage /> },
      { path: 'products', element: <ProductsPage /> },
      { path: 'shop-settings', element: <ShopSettingsPage /> },
      { path: 'inventory', element: <InventoryPage /> },
      { path: 'coupons', element: <CouponsPage /> },
      { path: 'discounts', element: <DiscountRulesPage /> },
      { path: 'sponsors', element: <SponsorsPage /> },
      { path: 'partners', element: <PartnersPage /> },
      { path: 'loyalty', element: <RewardsPage /> },
      { path: 'loyalty-periods', element: <LoyaltyPeriodsPage /> },
      { path: 'badges', element: <BadgesPage /> },
      { path: 'leaderboard', element: <LeaderboardPage /> },
      { path: 'news', element: <PostsPage /> },
      { path: 'rss-import', element: <RssImportPage /> },
      { path: 'polls', element: <PollsPage /> },
      { path: 'push', element: <PushPage /> },
      { path: 'audit', element: <ProtectedRoute roles={['CLUB_ADMIN']}><AuditPage /></ProtectedRoute> },
      { path: 'fans', element: <FansPage /> },
      { path: 'season-tickets', element: <SeasonTicketsPage /> },
      { path: 'receipts', element: <ReceiptsPage /> },
      { path: 'receipt-inspector', element: <ReceiptInspectorPage /> },
      { path: 'watchlist', element: <WatchlistPage /> },
      { path: 'checkin', element: <CheckinPage /> },
      { path: 'banned-words', element: <ProtectedRoute roles={['CLUB_ADMIN']}><BannedWordsPage /></ProtectedRoute> },
      { path: 'hero-banners', element: <HeroBannersPage /> },
      { path: 'media-library', element: <MediaLibraryPage /> },
      { path: 'analytics', element: <ProtectedRoute roles={['CLUB_ADMIN']}><Navigate to="/analytics/audience" replace /></ProtectedRoute> },
      { path: 'analytics/audience', element: <ProtectedRoute roles={['CLUB_ADMIN']}><AudiencePage /></ProtectedRoute> },
      { path: 'analytics/store', element: <ProtectedRoute roles={['CLUB_ADMIN']}><StorePage /></ProtectedRoute> },
      { path: 'analytics/loyalty', element: <ProtectedRoute roles={['CLUB_ADMIN']}><LoyaltyPage /></ProtectedRoute> },
      { path: 'analytics/sponsors', element: <ProtectedRoute roles={['CLUB_ADMIN']}><AnalyticsSponsorsPage /></ProtectedRoute> },
      { path: 'staff', element: <ProtectedRoute roles={['CLUB_ADMIN']}><StaffPage /></ProtectedRoute> },
      { path: 'settings', element: <ProtectedRoute roles={['CLUB_ADMIN']}><SettingsPage /></ProtectedRoute> },
      { path: 'settings/email-templates', element: <ProtectedRoute roles={['CLUB_ADMIN']}><EmailTemplatesPage /></ProtectedRoute> },
      { path: 'settings/legal-pages', element: <ProtectedRoute roles={['CLUB_ADMIN']}><LegalPagesPage /></ProtectedRoute> },
      { path: 'settings/ai', element: <ProtectedRoute roles={['DEVELOPER']}><AiSettingsPage /></ProtectedRoute> },
      {
        path: 'settings/email-templates/:eventType/:lang',
        element: <ProtectedRoute roles={['CLUB_ADMIN']}><EmailTemplateEditor /></ProtectedRoute>,
      },
      { path: 'payment-settings', element: <ProtectedRoute roles={['CLUB_ADMIN']}><PaymentSettingsPage /></ProtectedRoute> },
      // Pagina unificata: ex /tenant-config fuso in /settings (Configurazione tenant).
      { path: 'tenant-config', element: <Navigate to="/settings" replace /> },
      { path: 'setup', element: <ProtectedRoute roles={['SUPER_ADMIN']}><SetupPage /></ProtectedRoute> },
      { path: 'admin/tenants', element: <ProtectedRoute roles={['SUPER_ADMIN']}><TenantsPage /></ProtectedRoute> },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);
